import type { ChangeEvent } from 'react';
import { useCallback } from 'react';

import ShareOutlined from '@mui/icons-material/ShareOutlined';

import ShareDialog from '@/components/ShareDialog';
import getFactsheetLink from '@/features/Factsheet/utils/getFactsheetLink';
import Button from '@/shared/ui/primitives/Button';
import { normalizeSx } from '@/utils/normalizeSx';

import useShareButton from './useShareButton';

import type { Sx } from '@/types/helpers';

const buttonBaseSx: Sx = { width: 192, height: 48, fontSize: '16px' };

const ShareButton = ({
  title,
  additionalTitle,
  code,
  sx,
  iconSize = 24
}: {
  title?: string;
  additionalTitle?: string;
  code?: string;
  sx?: Sx;
  iconSize?: number;
}) => {
  const {
    modalOpen,
    setModalOpen,
    content,
    setContent,
    recipient,
    handleChangeRecipient,
    handleCopyToClipboard,
    sendEmail,
    emailError
  } = useShareButton(code);

  const shareUrl = getFactsheetLink(code);
  const headerSuffix = additionalTitle ? ` / ${additionalTitle}` : '';
  const header = `Share ${title}${headerSuffix}`;

  const handleClose = useCallback(() => {
    setModalOpen(false);
  }, [setModalOpen]);

  const handleContentChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setContent(e.target.value);
    },
    [setContent]
  );

  return (
    <>
      <Button
        color='secondary'
        sx={[buttonBaseSx, ...normalizeSx(sx)]}
        variant='contained'
        onClick={() => {
          setModalOpen(true);
        }}
      >
        <ShareOutlined
          sx={{
            width: iconSize,
            height: iconSize
          }}
        />
        Share
      </Button>
      <ShareDialog
        content={content}
        emailError={emailError}
        handleChangeRecipient={handleChangeRecipient}
        header={header}
        open={modalOpen}
        recipient={recipient}
        shareUrl={shareUrl}
        onClose={handleClose}
        onContentChange={handleContentChange}
        onCopyClick={handleCopyToClipboard}
        onSendEmail={sendEmail}
      />
    </>
  );
};

export default ShareButton;
